import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Search, Calendar, Banknote, Calculator, FileText, ArrowRight, ArrowLeft,
    CheckCircle, Loader2, AlertTriangle, Percent, Zap, ShieldCheck, Send, Info, UserPlus, BadgePercent, Plus, Trash2,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';
import { propsFundoModal } from '../../src/lib/backdropModal';
import {
    calcularAcordo, ErroCalculoAcordo, formatarBRL, paraCentavos, paraReais,
    type ResultadoAcordo,
} from '../../src/lib/parcelamentoCalculo.ts';
import {
    analisarGateway, docPagador, fraseDesconto, nomePagador, ORIGENS_DESCONTO, resolverContaCobranca, validarOrigemDesconto,
    validarOutrosDebitos,
    type OrigemDesconto, type OutroDebitoDigitado,
} from '../../src/lib/parcelamentoRegras.ts';
import {
    agruparPorPagador, filtrarPeloPagador, motivoForaDoAcordo, normalizarTexto, ordenarResultado, planejarMarcacao,
    STATUS_BOLETO_NEGOCIAVEL, STATUS_FATURA_NEGOCIAVEL,
} from '../../src/lib/parcelamentoBusca.ts';
import {
    anexoBPadrao, coobrigadoVazio, devedorPadrao, enderecoTexto, EVENTOS_QUEBRA, faturaParaTermo, formatarDocumento,
    mesSaldoDoInput, mesSaldoParaInput, origemValidadeSaldo,
    tipoPessoa, trocarEvento, validarDadosTermo,
    type AnexoBTermo, type CoobrigadoTermo, type DadosTermo, type DevedorTermo, type EnderecoPagador, type EventoQuebra,
} from '../../src/lib/termoNegociacao.ts';
import { buscarCadastroDevedor, criarAcordo, enviarTermoAcordo, descreverCanal } from '../../src/lib/parcelamentoAcordo';
import {
    dataEmBrasilia, erroVencimentoEntrada, mensagemEnvioEntrada, PRAZO_MINIMO_ENTRADA_DIAS,
} from '../../src/lib/entradaAcordoRegras.ts';
import { descricaoSugeridaAcordo } from '../../src/lib/vinculoAcordo.ts';
import { escolherPagador, Pagador } from '../../src/lib/pagadorBoleto';
import {
    defasagemDaUc, DIAS_PARA_SAIR_SEPARADA, mesDaFaturaDaParcela, motivoParaNaoSomar,
} from '../../supabase/functions/_shared/parcelaNaFaturaVigor.ts';
import MemoriaCalculoAcordo from './components/MemoriaCalculoAcordo';

/** UC do acordo e como o boleto mensal dela costuma vencer — decide se as
 *  parcelas podem ir somadas no boleto da Vigor. */
interface UcDoAcordo {
    id: number;
    situacao: string | null;
    /** Meses entre o mês da fatura e o vencimento do boleto (ver defasagemDaUc). */
    defasagem: number;
    /** Dia em que o último boleto mensal venceu, para sugerir o 1º vencimento. */
    diaVencimento: number | null;
}

/**
 * Criação de acordo de parcelamento com juros embutidos, entrada obrigatória e
 * termo assinado. Chamado #5686.
 *
 * ⚠️ MUDANÇA DE COMPORTAMENTO em relação à versão anterior desta tela.
 *
 * A versão antiga cancelava o boleto no Sicoob e carimbava 'Parcelado' nas
 * faturas no MESMO clique em que criava o acordo. Se o cliente sumisse depois,
 * a dívida original já tinha sido destruída e não havia como voltar.
 *
 * Agora nada disso acontece aqui. Esta tela cria a proposta, emite o boleto da
 * ENTRADA e manda o termo para assinatura. As faturas antigas continuam
 * vencidas, com boleto vivo. Quem move as faturas-mãe é a edge function
 * `efetivar-parcelamentos`, e só depois de assinatura do cliente + entrada
 * compensada — a regra que o chamado pediu ("o parcelamento só vai valer a
 * partir do momento que for assinado e pago a entrada").
 */

interface CriarParcelamentoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    preSelectedUc?: any;
    preSelectedFatura?: any;
    /** Várias faturas de uma vez — usado pelo deep-link da rota de cobrança, em
     *  que o cobrador negocia TODA a dívida da UC na porta do cliente. */
    preSelectedFaturas?: any[];
}

interface FaturaPendente {
    id: string;
    uc_codigo: string;
    cliente_nome: string;
    cliente_cpf_cnpj?: string | null;
    nome_cliente_boleto?: string | null;
    cpf_cnpj_cliente_boleto?: string | null;
    mes_referencia: string;
    valor_boleto_vigor: number;
    vencimento_boleto: string | null;
    status_boleto_vigor: string;
    status_fatura: string;
    num_cobranca_boleto?: string | null;
    conta_sicoob?: string | null;
    empresa_parceira_id?: string | null;
    end_logradouro?: string | null;
    end_numero?: string | null;
    end_bairro?: string | null;
    end_cidade?: string | null;
    end_uf?: string | null;
    end_cep?: string | null;
    /** Fatura do dono anterior da UC (troca de dono). */
    dono_anterior_troca_id?: string | null;
}

// Uma string só, sem `+`: o supabase-js tipa o resultado lendo o texto literal
// das colunas, e uma concatenação vira `string` e perde esse tipo.
const COLUNAS_FATURA_ACORDO = 'id, uc_codigo, cliente_nome, cliente_cpf_cnpj, nome_cliente_boleto, cpf_cnpj_cliente_boleto, mes_referencia, valor_boleto_vigor, vencimento_boleto, status_boleto_vigor, status_fatura, num_cobranca_boleto, conta_sicoob, empresa_parceira_id, end_logradouro, end_numero, end_bairro, end_cidade, end_uf, end_cep, dono_anterior_troca_id';

/** Filtro comum das duas consultas do passo 1 — a busca pelo que foi digitado e
 *  a expansão pelo CPF/CNPJ de quem paga.
 *
 *  Uma cópia divergente entre as duas deixaria a lista inconsistente consigo
 *  mesma: fatura que entra por um caminho e não pelo outro.
 *
 *  `conta_sicoob` e `empresa_parceira_id` vêm para a tela poder dizer em qual
 *  conta o acordo vai sair antes de ele existir; os documentos do titular e do
 *  pagador identificam de quem é a dívida, já que o nome varia de uma fatura
 *  para outra (ver `mesmoPagador`). */
const consultaFaturasPendentes = () => supabase
    .from('faturas')
    .select(COLUNAS_FATURA_ACORDO)
    .in('status_boleto_vigor', [...STATUS_BOLETO_NEGOCIAVEL])
    .gt('valor_boleto_vigor', 0)
    .in('status_fatura', [...STATUS_FATURA_NEGOCIAVEL])
    .is('parcelamento_origem_id', null)
    .order('vencimento_boleto', { ascending: true });

/** Fatura que chegou por atalho e não pode entrar no acordo, com o motivo. */
interface FaturaForaDoAcordo {
    id: string;
    mes: string;
    motivo: string;
}

/**
 * Relê do banco as faturas que chegam por atalho e separa as que podem entrar.
 *
 * Quem chama manda a fatura no formato dele: a rota de cobrança manda o que
 * mostra no card (sem nome, sem CPF, sem `valor_boleto_vigor` — o valor vem em
 * `valor`), e o link do WhatsApp manda meia dúzia de colunas. Semeado assim, o
 * passo 1 mostrava "SEM NOME · R$ 0,00" e a busca começava vazia (30/09/2026).
 * Aqui a fatura vem inteira, com as colunas da busca, e passa pela mesma regra
 * dela — parcela de outro acordo ou boleto já pago não entram calados.
 */
async function lerFaturasDoAtalho(ids: string[]): Promise<{ elegiveis: FaturaPendente[]; fora: FaturaForaDoAcordo[] }> {
    const { data, error } = await supabase
        .from('faturas')
        .select(`${COLUNAS_FATURA_ACORDO}, parcelamento_origem_id, data_pgto_boleto, quitado_em`)
        .in('id', ids);
    if (error) throw error;
    const porId = new Map((data || []).map((f: any) => [String(f.id), f]));
    const elegiveis: FaturaPendente[] = [];
    const fora: FaturaForaDoAcordo[] = [];
    for (const id of ids) {
        const f = porId.get(id);
        if (!f) { fora.push({ id, mes: `#${id}`, motivo: 'não encontrada' }); continue; }
        const motivo = motivoForaDoAcordo(f);
        if (motivo) fora.push({ id, mes: f.mes_referencia || `#${id}`, motivo });
        else elegiveis.push(f as FaturaPendente);
    }
    return { elegiveis, fora };
}

/** Data de HOJE no fuso de quem está usando o sistema.
 *
 *  `toISOString()` devolve UTC: em Brasília (UTC−3), das 21h em diante ele já
 *  responde a data de amanhã. Isso é a data-base dos juros de mora pro rata die
 *  e o vencimento sugerido da entrada — um dia a mais cobrado do cliente porque
 *  o analista fechou o acordo à noite. */
const dataLocalIso = (d: Date) => {
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
};
const hojeIso = () => dataLocalIso(new Date());
const emDias = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return dataLocalIso(d);
};

const formatDateBR = (s: string | null | undefined) => {
    if (!s) return '-';
    const [a, m, d] = s.split('T')[0].split('-');
    return `${d}/${m}/${a}`;
};

/** Mesma regra de normalização de UC de FinanceiroLayout. */
const normalizeUcLocal = (uc: any): string => {
    if (typeof uc !== 'string' && typeof uc !== 'number') return '';
    return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
};

/** O boleto sai em nome de outra pessoa que não o titular da UC? */
const titularDiferente = (f: FaturaPendente) =>
    !!f.nome_cliente_boleto && normalizarTexto(nomePagador(f)) !== normalizarTexto(f.cliente_nome);

const CAMPO = 'w-full px-3 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm';
const ROTULO = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide flex items-center gap-1.5 mb-1';

const CriarParcelamentoModal: React.FC<CriarParcelamentoModalProps> = ({
    isOpen, onClose, onSuccess, preSelectedUc, preSelectedFatura, preSelectedFaturas,
}) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // ---- passo 1: seleção
    const [selectedUc, setSelectedUc] = useState<any>(null);
    const [faturaSearchTerm, setFaturaSearchTerm] = useState('');
    const [faturasPendentes, setFaturasPendentes] = useState<FaturaPendente[]>([]);
    // Fonte única da seleção. Havia um `Set` de ids em paralelo a este Map; dois
    // estados para o mesmo fato só rendem chance de dessincronizar.
    const [selectedFaturasData, setSelectedFaturasData] = useState<Map<string, FaturaPendente>>(new Map());
    // Assinatura do último resultado de busca já marcado automaticamente — é o
    // que impede a marcação de voltar a cada render e desfazer o que o operador
    // desmarcou na mão.
    const [buscaJaMarcada, setBuscaJaMarcada] = useState('');
    // O termo que produziu `faturasPendentes`. A marcação usa este, não o campo:
    // enquanto o operador digita, o campo já mudou e o resultado ainda é o velho.
    const [termoResultado, setTermoResultado] = useState('');
    // Ids que a TELA marcou sozinha na última busca. Saem na próxima busca se o
    // operador não mexeu — era o acúmulo delas, busca após busca enquanto se
    // digitava, que enchia o acordo de nomes que ninguém escreveu.
    const [autoMarcadas, setAutoMarcadas] = useState<Set<string>>(new Set());
    const [pagadorMarcado, setPagadorMarcado] = useState<string | null>(null);
    const [loadingFaturas, setLoadingFaturas] = useState(false);
    // Faturas do atalho (rota de cobrança, link, botão da fatura) relidas do banco.
    const [lendoAtalho, setLendoAtalho] = useState(false);
    const [atalhoFora, setAtalhoFora] = useState<FaturaForaDoAcordo[]>([]);

    // ---- passo 2: condições
    // O que o analista escreveu na descrição; null = não mexeu, vale a sugestão.
    const [descricaoDigitada, setDescricaoDigitada] = useState<string | null>(null);
    const [cobrarEncargos, setCobrarEncargos] = useState(false);
    const [multaPercent, setMultaPercent] = useState(2);
    const [moraPercent, setMoraPercent] = useState(1);

    const [negociarSaldo, setNegociarSaldo] = useState(false);
    const [saldoDisponivel, setSaldoDisponivel] = useState<number | null>(null);
    const [saldoKwh, setSaldoKwh] = useState<number | ''>('');
    const [saldoTarifa, setSaldoTarifa] = useState<number | ''>('');
    const [saldoDesconto, setSaldoDesconto] = useState<number | ''>(18);

    // Desconto na dívida inteira (pedido de 30/09/2026). Digitável em R$ OU em %,
    // no mesmo espelho da entrada: o campo que não está sendo digitado é derivado
    // do resultado do motor.
    const [darDesconto, setDarDesconto] = useState(false);
    const [descontoModo, setDescontoModo] = useState<'valor' | 'percentual'>('percentual');
    const [descontoValorDigitado, setDescontoValorDigitado] = useState<number | ''>('');
    const [descontoPercentDigitado, setDescontoPercentDigitado] = useState<number | ''>('');
    // De onde veio o desconto (chamado #6174). Sem padrão de propósito: quem dá o
    // desconto tem que escolher, senão o histórico volta a misturar as duas coisas.
    const [descontoOrigem, setDescontoOrigem] = useState<OrigemDesconto | null>(null);
    const [descontoDescricao, setDescontoDescricao] = useState('');

    // Débitos que não estão no sistema como fatura (multa de distrato, taxa...),
    // cobrados junto no acordo. Pedido do João Henrique, 08/10/2026.
    const [somarOutros, setSomarOutros] = useState(false);
    const [outrosDebitos, setOutrosDebitos] = useState<OutroDebitoDigitado[]>([{ descricao: '', valor: '' }]);

    // Entrada é digitável em R$ OU em %, espelhados como na tela da Equatorial.
    // `entradaModo` diz qual dos dois o analista está comandando; o outro é
    // DERIVADO no render a partir do resultado do motor — não é estado.
    // Guardar os dois em estado e sincronizá-los por efeito faz um campo zerar o
    // outro no meio da digitação e ainda provoca um render a mais por tecla.
    const [entradaModo, setEntradaModo] = useState<'valor' | 'percentual'>('percentual');
    const [entradaValorDigitado, setEntradaValorDigitado] = useState<number | ''>('');
    const [entradaPercentDigitado, setEntradaPercentDigitado] = useState<number | ''>(25);
    const [vencimentoEntrada, setVencimentoEntrada] = useState(emDias(5));

    const [taxaJuros, setTaxaJuros] = useState<number | ''>(1);
    const [modoPrazo, setModoPrazo] = useState<'por_numero' | 'por_valor'>('por_numero');
    const [numeroParcelas, setNumeroParcelas] = useState<number | ''>(10);
    const [valorParcela, setValorParcela] = useState<number | ''>('');
    const [primeiroVencimento, setPrimeiroVencimento] = useState(emDias(35));

    // Quem assina o termo, pela MESMA cascata do boleto (src/lib/pagadorBoleto.ts):
    // assinante da negociação → cadastro da UC. A fatura fica de último recurso e
    // longe daqui de propósito — ver `assinanteDoTermo`.
    const [assinante, setAssinante] = useState<Pagador | null>(null);

    // ---- Anexo B (só com saldo). O mês do saldo nasce vazio: quem faz o acordo
    // informa (#6185). A fatura-fonte é a última com saldo, achada sozinha.
    const [anexoB, setAnexoB] = useState<AnexoBTermo>(() => anexoBPadrao({ mesSaldo: '' }));
    const [faturaFonteSaldo, setFaturaFonteSaldo] = useState('');

    // ---- passo 3: partes do termo
    // Devedor = o titular (cadastro de hoje). Quando outra pessoa assume a
    // dívida — comprou o estabelecimento, por exemplo —, ela entra como
    // COOBRIGADO solidário (Anexo D) e assina junto; o titular não sai do termo
    // (Cláusula 1.3: a entrada de um terceiro não libera o devedor original).
    const [devedor, setDevedor] = useState<DevedorTermo>(() => devedorPadrao({}));
    const [temCoobrigado, setTemCoobrigado] = useState(false);
    const [coobrigado, setCoobrigado] = useState<CoobrigadoTermo>(coobrigadoVazio);
    // Boletos do acordo no nome do responsável (decisão do Vinícius, 23/09/2026:
    // quem comprou o estabelecimento é quem paga). Ligado por padrão quando há
    // responsável; o analista desliga se o titular continuar pagando.
    const [boletoNoResponsavel, setBoletoNoResponsavel] = useState(true);
    const [enviarTermo, setEnviarTermo] = useState(true);
    // Parcelas somadas no boleto mensal da Vigor em vez de fatura avulsa (17/10/2026).
    const [parcelasNaFatura, setParcelasNaFatura] = useState(false);
    const [ucDoAcordo, setUcDoAcordo] = useState<UcDoAcordo | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setStep(1);
        setParcelasNaFatura(false);
        setDescricaoDigitada(null);
        setCobrarEncargos(false);
        setMultaPercent(2);
        setMoraPercent(1);
        setNegociarSaldo(false);
        setSaldoDisponivel(null);
        setSaldoKwh('');
        setSaldoTarifa('');
        setSaldoDesconto(18);
        setDarDesconto(false);
        setDescontoModo('percentual');
        setDescontoValorDigitado('');
        setDescontoPercentDigitado('');
        setDescontoOrigem(null);
        setDescontoDescricao('');
        setSomarOutros(false);
        setOutrosDebitos([{ descricao: '', valor: '' }]);
        setEntradaModo('percentual');
        setEntradaValorDigitado('');
        setEntradaPercentDigitado(25);
        setVencimentoEntrada(emDias(5));
        setTaxaJuros(1);
        setModoPrazo('por_numero');
        setNumeroParcelas(10);
        setValorParcela('');
        setPrimeiroVencimento(emDias(35));
        setAnexoB(anexoBPadrao({ mesSaldo: '' }));
        setFaturaFonteSaldo('');
        setDevedor(devedorPadrao({}));
        setTemCoobrigado(false);
        setCoobrigado(coobrigadoVazio());
        setBoletoNoResponsavel(true);
        setEnviarTermo(true);
        setFaturasPendentes([]);
        setSelectedFaturasData(new Map());
        setBuscaJaMarcada('');
        setTermoResultado('');
        setAutoMarcadas(new Set());
        setPagadorMarcado(null);
        setAtalhoFora([]);
        setLendoAtalho(false);

        const atalho: any[] = preSelectedFaturas?.length ? preSelectedFaturas : preSelectedFatura ? [preSelectedFatura] : [];
        if (atalho.length > 0) {
            // As faturas são relidas do banco no efeito abaixo; aqui só começa a
            // busca pelo nome, que pode vir só na UC — a rota de cobrança manda
            // o nome do cliente ali, não na fatura.
            const primeira = atalho[0];
            setFaturaSearchTerm(
                primeira.nome_cliente_boleto || primeira.cliente_nome || preSelectedUc?.nome_cliente
                || primeira.uc_codigo || preSelectedUc?.numero_uc || '',
            );
            setSelectedUc(null);
        } else if (preSelectedUc) {
            setSelectedUc(preSelectedUc);
            setFaturaSearchTerm(preSelectedUc.nome_cliente || '');
        } else {
            setSelectedUc(null);
            setFaturaSearchTerm('');
        }
    }, [isOpen]);

    // Faturas do atalho, relidas do banco — ver `lerFaturasDoAtalho`. A chave é
    // texto porque quem chama recria o array a cada render, e reler por isso
    // desfaria o que o operador já marcou.
    const idsAtalho = (preSelectedFaturas?.length ? preSelectedFaturas : preSelectedFatura ? [preSelectedFatura] : [])
        .map((f: any) => String(f.id)).join(',');
    useEffect(() => {
        if (!isOpen || !idsAtalho) return;
        let cancelado = false;
        setLendoAtalho(true);
        lerFaturasDoAtalho(idsAtalho.split(','))
            .then(({ elegiveis, fora }) => {
                if (cancelado) return;
                // Soma ao que já está marcado: se a busca pelo nome chegou antes,
                // o que ela marcou fica. As do atalho viram escolha do operador e
                // não saem na próxima busca.
                setSelectedFaturasData(prev => {
                    const n = new Map(prev);
                    for (const f of elegiveis) n.set(String(f.id), f);
                    return n;
                });
                setAutoMarcadas(prev => {
                    const n = new Set(prev);
                    for (const f of elegiveis) n.delete(String(f.id));
                    return n;
                });
                setAtalhoFora(fora);
                const nome = elegiveis[0]?.nome_cliente_boleto || elegiveis[0]?.cliente_nome;
                if (nome) setFaturaSearchTerm(prev => (prev.trim() ? prev : nome));
            })
            .catch(err => {
                if (cancelado) return;
                console.error('[parcelamento] releitura das faturas do atalho', err);
                addToast('Não consegui carregar as faturas escolhidas. Busque o cliente pelo nome ou pela UC.', 'error', 10000);
            })
            .finally(() => { if (!cancelado) setLendoAtalho(false); });
        return () => { cancelado = true; };
        // `addToast` vem do contexto e não muda; nas dependências só releria à toa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, idsAtalho]);

    // Busca de faturas por nome ou código de UC (resolve condomínio e UC sem cadastro).
    useEffect(() => {
        const termo = faturaSearchTerm.trim();
        if (termo.length < 3) { setFaturasPendentes([]); setTermoResultado(''); return; }
        const buscar = async () => {
            setLoadingFaturas(true);
            try {
                // Vírgula e parênteses quebram a sintaxe do filtro `or` do PostgREST.
                const termoSeguro = termo.replace(/[,()]/g, ' ');
                const termoUc = normalizeUcLocal(termoSeguro);
                const termoDoc = termo.replace(/\D/g, '');
                // uc_codigo_norm (coluna gerada) casa a UC com ou sem máscara:
                // '3.071.720.012-00' e '307172001200' são o mesmo número.
                // Os dois documentos estão gravados só com dígitos em todas as
                // linhas preenchidas, então CPF/CNPJ digitado casa por igualdade.
                //
                // Titular E pagador: a UC pode estar no nome de um (o dono do
                // imóvel) e o boleto sair no de outro (o inquilino). Buscar só o
                // titular escondia a dívida de quem paga — o caso de 228 das
                // 1.338 faturas em aberto.
                const clausulas = [
                    `cliente_nome.ilike.%${termoSeguro}%`,
                    `nome_cliente_boleto.ilike.%${termoSeguro}%`,
                ];
                if (termoUc) clausulas.push(`uc_codigo_norm.ilike.%${termoUc}%`);
                if (termoDoc.length === 11 || termoDoc.length === 14) {
                    clausulas.push(`cliente_cpf_cnpj.eq.${termoDoc}`, `cpf_cnpj_cliente_boleto.eq.${termoDoc}`);
                }

                const { data, error } = await consultaFaturasPendentes()
                    .or(clausulas.join(','))
                    .limit(50);
                if (error) throw error;
                const achadas = (data || []) as FaturaPendente[];

                // Segunda passada pelo CPF/CNPJ de quem paga as que apareceram.
                //
                // Buscar por nome não devolve a dívida inteira de quem tem mais
                // de uma UC: 120 dos 423 titulares com débito estão nessa
                // situação, e em 14 deles o nome do cliente muda de uma fatura
                // para outra ("JOSE DA SILVA" e "JOSE DA SILVA ME"), de modo que
                // parte da dívida nunca entrava na lista. O acordo é de quem
                // paga — a lista tem que mostrar tudo o que ele deve. Procura o
                // documento nas duas colunas: fatura sem pagador gravado é
                // cobrada do titular.
                //
                // É uma consulta só, com os documentos da primeira página. Não
                // dá para pedir em paralelo com a de cima: quais documentos
                // procurar é exatamente o que a primeira responde.
                const documentos = [...new Set(achadas.map(docPagador).filter(Boolean))].slice(0, 20);
                let completas = achadas;
                if (documentos.length > 0) {
                    const lista = documentos.join(',');
                    const { data: doPagador } = await consultaFaturasPendentes()
                        .or(`cpf_cnpj_cliente_boleto.in.(${lista}),cliente_cpf_cnpj.in.(${lista})`)
                        .limit(200);
                    const porId = new Map(achadas.map(f => [String(f.id), f]));
                    // A consulta acha o documento como pagador OU como titular.
                    // Onde ele é só o titular, a dívida é de outra pessoa — e era
                    // essa linha que trazia nomes que o operador não digitou.
                    const doMesmoPagador = filtrarPeloPagador((doPagador || []) as FaturaPendente[], documentos);
                    for (const f of doMesmoPagador) porId.set(String(f.id), f);
                    completas = Array.from(porId.values());
                }

                // Quem mais parece com o que foi digitado vem primeiro; dentro
                // de cada pagador, do mês mais antigo para o mais novo.
                setFaturasPendentes(ordenarResultado(termo, completas));
                setTermoResultado(termo);
            } catch (err) {
                console.error('Erro ao buscar faturas:', err);
            } finally {
                setLoadingFaturas(false);
            }
        };
        const t = setTimeout(buscar, 400);
        return () => clearTimeout(t);
    }, [faturaSearchTerm]);

    // Marca ou desmarca de uma vez todas as faturas de um pagador (botão do grupo).
    // É decisão do operador: as marcas automáticas que estiverem na tela passam
    // a ser dele e não saem mais na próxima busca.
    const marcarGrupo = (grupo: FaturaPendente[], marcar: boolean) => {
        setSelectedFaturasData(prev => {
            const n = new Map(prev);
            for (const f of grupo) {
                if (marcar) n.set(String(f.id), f); else n.delete(String(f.id));
            }
            return n;
        });
        setAutoMarcadas(new Set());
    };

    // Marcação automática: toda a dívida de quem paga já vem escolhida.
    //
    // Quem abre esta tela está negociando com UMA pessoa, e o normal é fechar
    // acordo sobre tudo o que ela deve — não sobre um mês solto. Com a lista
    // vindo desmarcada, o operador clicava mês a mês; esquecer um deixava dívida
    // fora do termo, e ela seguia vencida ao lado de um acordo que, no papel,
    // "resolveu tudo".
    //
    // QUAL pagador é a regra de `planejarMarcacao` (parcelamentoBusca.ts): o que
    // foi digitado, nunca o primeiro da lista em ordem alfabética — era isso que
    // marcava gente de outro nome. Empate não marca ninguém.
    //
    // O ajuste roda durante o render, não num efeito: assim a lista já pinta
    // marcada, sem o piscar de um segundo render. `buscaJaMarcada` faz isso
    // valer uma vez por resultado de busca — o operador segue livre para
    // desmarcar o que não entra.
    const assinaturaBusca = `${termoResultado}|${faturasPendentes.map(f => String(f.id)).join(',')}`;
    if (termoResultado && assinaturaBusca !== buscaJaMarcada) {
        setBuscaJaMarcada(assinaturaBusca);
        const plano = planejarMarcacao(termoResultado, faturasPendentes, selectedFaturasData.keys(), autoMarcadas);
        const marcar = new Set(plano.marcar);
        setSelectedFaturasData(prev => {
            const n = new Map(prev);
            for (const id of plano.desmarcar) n.delete(id);
            for (const f of faturasPendentes) if (marcar.has(String(f.id))) n.set(String(f.id), f);
            return n;
        });
        setAutoMarcadas(marcar);
        setPagadorMarcado(plano.referencia ? nomePagador(plano.referencia) : null);
    }

    const selecionadas = useMemo(() => Array.from(selectedFaturasData.values()), [selectedFaturasData]);

    // Conta bancária, empresa e UC que o acordo vai usar. Mesma função que o
    // serviço chama na hora de criar a cobrança — a tela não pode prometer uma
    // conta e o acordo sair em outra.
    const contaCobranca = useMemo(() => resolverContaCobranca(selecionadas), [selecionadas]);
    const totalSelecionadoCentavos = useMemo(
        () => selecionadas.reduce((soma, f) => soma + paraCentavos(Number(f.valor_boleto_vigor) || 0), 0),
        [selecionadas],
    );
    // A fatura que define conta, empresa, UC e nome do pagador do acordo.
    const faturaReferencia = useMemo(
        () => selecionadas.find(f => String(f.id) === contaCobranca.faturaId) || selecionadas[0] || null,
        [selecionadas, contaCobranca.faturaId],
    );
    // Buscar por nome parcial ("MARIA") devolve pagadores diferentes na mesma
    // lista. A marcação automática só pega um deles — a tela precisa dizer qual,
    // ou o operador conta 4 linhas marcadas e acha que é a dívida toda.
    //
    // Conta com a MESMA regra que marca. Contar documento e nome como chaves
    // separadas dizia "2 titulares" para uma pessoa só, quando parte das
    // faturas dela não tem CPF gravado.
    const pagadoresNaLista = useMemo(() => agruparPorPagador(faturasPendentes).length, [faturasPendentes]);
    const ucCodigoBase = contaCobranca.uc_codigo || selecionadas[0]?.uc_codigo || selectedUc?.numero_uc || '';

    // Saldo de energia e tarifa de referência da UC. O estoque é `saldo_ugs_vigor`
    // — `saldo_acumulado_kwh` é outra coisa e daria um número errado aqui.
    useEffect(() => {
        if (!ucCodigoBase || !isOpen) return;
        let cancelado = false;
        (async () => {
            const { data } = await supabase
                .from('faturas')
                .select('saldo_ugs_vigor, desconto_tarifa, consumo_scee_tarifa_c_imposto, mes_referencia')
                .eq('uc_codigo', ucCodigoBase)
                .not('saldo_ugs_vigor', 'is', null)
                .order('created_at', { ascending: false })
                .limit(1);
            if (cancelado) return;
            const linha = (data || [])[0];
            if (!linha) return;
            setSaldoDisponivel(Number(linha.saldo_ugs_vigor) || 0);
            // Fatura-fonte do Anexo B: o último ciclo, de onde saem saldo e tarifa.
            setFaturaFonteSaldo(String(linha.mes_referencia || ''));
            if (linha.consumo_scee_tarifa_c_imposto) {
                setSaldoTarifa(prev => (prev === '' ? Number(linha.consumo_scee_tarifa_c_imposto) : prev));
            }
            // `faturas.desconto_tarifa` é fração (0.2 = 20%) em toda a base; o campo da
            // tela é percentual. Sem o ×100 o acordo usava 0,2% de desconto e cobrava
            // os créditos quase cheios (UC 10031291838: R$ 106,71 a mais, 21/09/2026).
            if (linha.desconto_tarifa) {
                const descontoPercent = Math.round(Number(linha.desconto_tarifa) * 10000) / 100;
                setSaldoDesconto(prev => (prev === 18 ? descontoPercent : prev));
            }
        })();
        return () => { cancelado = true; };
    }, [ucCodigoBase, isOpen]);

    /**
     * Assinante do termo — cadastro de HOJE, não o nome carimbado na fatura.
     *
     * Caso que originou (21/09/2026, UC 10031291838): as faturas guardavam
     * "EDUARDO DE LIMA CRUVINEL", titular da conta de luz na Equatorial, e o
     * associado é outro. O cadastro da UC já tinha sido corrigido, mas o acordo
     * lia a fatura — e o Termo de Confissão de Dívida saiu no nome errado, com o
     * cliente a um clique de assinar. Titular da conta ≠ assinante do termo é
     * normal no modelo (ver pagadorBoleto.ts); quem confessa a dívida é o
     * assinante. A fatura só vale como último recurso, e a tela avisa quando é
     * ela que está valendo.
     */
    //
    // O mesmo cadastro pré-preenche o DEVEDOR do termo inteiro: nome, documento,
    // endereço, e-mail e WhatsApp. Antes só vinham e-mail e WhatsApp quando a
    // tela era aberta a partir da UC — pela fatura (o caminho mais usado) os dois
    // campos chegavam vazios e o analista tinha de ir procurar.
    const faturaReferenciaId = faturaReferencia?.id ?? null;
    // As outras UCs da seleção: o cadastro pode estar só numa delas.
    const ucsSelecionadas = useMemo(
        () => [...new Set(selecionadas.map(f => f.uc_codigo).filter(Boolean))].join('|'),
        [selecionadas],
    );
    // Todas as faturas escolhidas são do mesmo dono anterior da UC (troca de dono):
    // quem confessa a dívida é ele, não quem comprou o estabelecimento.
    const trocaDonoDaSelecao = useMemo(() => {
        const ids = new Set(selecionadas.map(f => f.dono_anterior_troca_id ?? null));
        return ids.size === 1 ? [...ids][0] : null;
    }, [selecionadas]);

    // UC do acordo: situação no rateio e como o boleto mensal dela vence. Mesma
    // busca que handleSalvar faz para gravar o vínculo com o cadastro.
    useEffect(() => {
        if (!isOpen) { setUcDoAcordo(null); return; }
        const norm = normalizeUcLocal(ucCodigoBase);
        if (!selectedUc?.id && !norm) { setUcDoAcordo(null); return; }
        let cancelado = false;
        (async () => {
            const base = supabase.from('unidades_consumidoras').select('id, situacao, numero_uc, outro_numero_uc');
            const { data: ucs } = await (selectedUc?.id
                ? base.eq('id', selectedUc.id)
                : base.or(`numero_uc_norm.eq."${norm}",outro_numero_uc_norm.eq."${norm}"`).order('id')
            ).limit(1);
            const uc = ucs?.[0];
            if (cancelado) return;
            if (!uc) { setUcDoAcordo(null); return; }
            const codigos = [...new Set([uc.numero_uc, uc.outro_numero_uc, ucCodigoBase].filter(Boolean))];
            const { data: recentes } = await supabase
                .from('faturas')
                .select('mes_referencia, vencimento_boleto')
                .in('uc_codigo', codigos)
                .is('parcelamento_origem_id', null)
                .or('classificacao.is.null,classificacao.neq.AVULSO')
                .not('vencimento_boleto', 'is', null)
                .order('created_at', { ascending: false })
                .limit(6);
            if (cancelado) return;
            const ultimoVencimento = recentes?.[0]?.vencimento_boleto;
            setUcDoAcordo({
                id: uc.id,
                situacao: uc.situacao ?? null,
                defasagem: defasagemDaUc(recentes || []),
                diaVencimento: ultimoVencimento ? Number(String(ultimoVencimento).slice(8, 10)) : null,
            });
        })();
        return () => { cancelado = true; };
    }, [isOpen, ucCodigoBase, selectedUc?.id]);

    // Somar no boleto da Vigor só vale para quem recebe esse boleto todo mês, no
    // próprio nome. O responsável pela dívida é escolhido no passo 3: se ele
    // entrar depois, as parcelas voltam a sair separadas e o resumo avisa.
    const motivoNaoSomar = motivoParaNaoSomar({
        ucId: ucDoAcordo?.id ?? null,
        situacaoUc: ucDoAcordo?.situacao,
        boletoNoResponsavel: temCoobrigado && boletoNoResponsavel,
        donoAnterior: !!trocaDonoDaSelecao,
        empresaParceira: selecionadas.some(f => !!f.empresa_parceira_id),
    });
    const somarNaFatura = parcelasNaFatura && !motivoNaoSomar;
    useEffect(() => {
        if (!isOpen) { setAssinante(null); return; }
        const norm = normalizeUcLocal(ucCodigoBase);
        if (!selectedUc?.id && !norm) { setAssinante(null); return; }
        let cancelado = false;
        (async () => {
            const { uc, negociacao } = await buscarCadastroDevedor({
                ucId: selectedUc?.id ?? null,
                ucCodigo: ucCodigoBase,
                ucCodigos: ucsSelecionadas.split('|'),
                trocaDonoId: trocaDonoDaSelecao,
            });
            if (cancelado) return;

            const escolhido = escolherPagador([
                { origem: 'assinante (termo Vigor)', nome: negociacao?.cliente_nome, documento: negociacao?.cpf_cnpj },
                { origem: 'cadastro da UC', nome: uc?.nome_cliente, documento: uc?.cpf_cnpj_cliente },
            ]);
            setAssinante(escolhido.nome ? escolhido : null);
            setDevedor(devedorPadrao({ negociacao, uc, fatura: faturaReferencia }));
            setAnexoB(prev => ({
                ...prev,
                termoAdesao: anexoBPadrao({ dataAssinaturaTermo: negociacao?.data_assinatura_termo }).termoAdesao,
            }));
        })();
        return () => { cancelado = true; };
        // faturaReferencia entra pelo id: o objeto muda de identidade a cada
        // seleção, e refazer a consulta por isso apagaria o que foi editado.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, ucCodigoBase, selectedUc?.id, faturaReferenciaId, ucsSelecionadas, trocaDonoDaSelecao]);

    // O mês da fatura-fonte entra no Anexo B quando chega (vem de outra consulta).
    // O texto da origem do saldo é montado em `dadosTermo`, junto com o mês do saldo.
    useEffect(() => {
        if (!faturaFonteSaldo) return;
        setAnexoB(prev => (prev.faturaFonte ? prev : { ...prev, faturaFonte: faturaFonteSaldo }));
    }, [faturaFonteSaldo]);

    const nomeNaFatura = faturaReferencia?.nome_cliente_boleto || faturaReferencia?.cliente_nome || '';
    const chaveNome = (v: string) => v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase().replace(/\s+/g, ' ').trim();
    const faturaComOutroNome = !!devedor.nome && !!nomeNaFatura && chaveNome(nomeNaFatura) !== chaveNome(devedor.nome);
    const devedorPj = tipoPessoa(devedor.documento) === 'PJ';

    // O que o termo vai dizer das partes — é isto que vai gravado no acordo.
    const dadosTermo: DadosTermo = useMemo(() => ({
        versao: 1,
        devedor: {
            ...devedor,
            representante: devedorPj ? (devedor.representante ?? { nome: '', cpf: '', poderes: 'contrato social' }) : null,
            coobrigacaoPf: devedorPj ? devedor.coobrigacaoPf : null,
        },
        coobrigado: temCoobrigado ? coobrigado : null,
        anexoB: negociarSaldo ? { ...anexoB, origemValidade: origemValidadeSaldo(anexoB) } : null,
        faturas: selecionadas.map(faturaParaTermo),
        boletoNoResponsavel: temCoobrigado && boletoNoResponsavel,
        desconto: darDesconto && descontoOrigem ? { origem: descontoOrigem, descricao: descontoDescricao.trim() } : null,
    }), [devedor, devedorPj, temCoobrigado, coobrigado, negociarSaldo, anexoB, selecionadas, boletoNoResponsavel,
        darDesconto, descontoOrigem, descontoDescricao]);
    const errosTermo = useMemo(
        () => validarDadosTermo(dadosTermo, { exigirContato: enviarTermo, temSaldo: negociarSaldo }),
        [dadosTermo, enviarTermo, negociarSaldo],
    );

    // Descrição sugerida a partir dos meses escolhidos — sem sobrescrever o que
    // o analista já escreveu. Sai impressa no boleto da entrada e das parcelas.
    // Antes ela congelava no primeiro mês marcado: o atalho "Parcelar cobrança"
    // abre com uma fatura e as outras entram depois, e o acordo do Gilberto Rosa
    // (JUN a SET/2026) saiu dizendo só "AGO/2026" (#6260).
    const descricao = descricaoDigitada ?? descricaoSugeridaAcordo(selecionadas.map(f => f.mes_referencia));

    // Avisos, não travas. Juntar contas Sicoob, empresas ou UCs diferentes num
    // acordo só é permitido: a dívida é de quem paga. O que a tela faz é dizer em
    // qual conta o acordo vai sair.
    const avisosGateway = useMemo(() => analisarGateway(selecionadas), [selecionadas]);

    // ------------------------------------------------------------- o cálculo
    const { resultado, erroCalculo } = useMemo<{ resultado: ResultadoAcordo | null; erroCalculo: string | null }>(() => {
        if (selecionadas.length === 0 && !negociarSaldo && !somarOutros) return { resultado: null, erroCalculo: null };
        try {
            const r = calcularAcordo({
                faturas: selecionadas.map(f => ({
                    id: f.id,
                    valor: Number(f.valor_boleto_vigor) || 0,
                    vencimento: f.vencimento_boleto,
                })),
                encargos: cobrarEncargos
                    ? { multaPercent: Number(multaPercent) || 0, moraMensalPercent: Number(moraPercent) || 0 }
                    : null,
                saldo: negociarSaldo && saldoKwh !== '' && saldoTarifa !== ''
                    ? { kwh: Number(saldoKwh), tarifa: Number(saldoTarifa), descontoPercent: Number(saldoDesconto) || 0 }
                    : null,
                outrosDebitos: somarOutros
                    ? outrosDebitos.map(d => ({ descricao: d.descricao, valor: Number(d.valor) || 0 }))
                    : null,
                desconto: !darDesconto ? null
                    : descontoModo === 'valor'
                        ? { valor: Number(descontoValorDigitado) || 0 }
                        : { percentual: Number(descontoPercentDigitado) || 0 },
                entrada: entradaModo === 'valor'
                    ? { valor: Number(entradaValorDigitado) || 0 }
                    : { percentual: Number(entradaPercentDigitado) || 0 },
                taxaJurosMensalPercent: Number(taxaJuros) || 0,
                parcelamento: modoPrazo === 'por_numero'
                    ? { modo: 'por_numero', numeroParcelas: Number(numeroParcelas) || 0 }
                    : { modo: 'por_valor', valorParcela: Number(valorParcela) || 0 },
                dataBase: hojeIso(),
                primeiroVencimento,
                vencimentoEntrada,
            });
            return { resultado: r, erroCalculo: null };
        } catch (err: any) {
            return {
                resultado: null,
                erroCalculo: err instanceof ErroCalculoAcordo ? err.message : (err?.message || String(err)),
            };
        }
    }, [
        selecionadas, cobrarEncargos, multaPercent, moraPercent, negociarSaldo, saldoKwh, saldoTarifa,
        saldoDesconto, somarOutros, outrosDebitos, darDesconto, descontoModo, descontoValorDigitado, descontoPercentDigitado,
        entradaModo, entradaValorDigitado, entradaPercentDigitado, taxaJuros, modoPrazo,
        numeroParcelas, valorParcela, primeiroVencimento, vencimentoEntrada,
    ]);

    // Faturas da UC em que a primeira e a última parcela entram, quando somadas.
    const faixaFaturas = somarNaFatura && ucDoAcordo && resultado && resultado.parcelas.length > 0
        ? {
            de: mesDaFaturaDaParcela(resultado.parcelas[0].dataVencimento, ucDoAcordo.defasagem),
            ate: mesDaFaturaDaParcela(resultado.parcelas[resultado.parcelas.length - 1].dataVencimento, ucDoAcordo.defasagem),
        }
        : null;

    // O campo que o analista NÃO está comandando mostra o valor derivado.
    const descontoValorExibido = descontoModo === 'valor'
        ? descontoValorDigitado
        : (resultado ? Number(paraReais(resultado.descontoDividaCentavos).toFixed(2)) : '');
    const descontoPercentExibido = descontoModo === 'percentual'
        ? descontoPercentDigitado
        : (resultado ? Number(resultado.descontoDividaPercentual.toFixed(2)) : '');
    const entradaValorExibido = entradaModo === 'valor'
        ? entradaValorDigitado
        : (resultado ? Number(paraReais(resultado.entradaCentavos).toFixed(2)) : '');
    const entradaPercentExibido = entradaModo === 'percentual'
        ? entradaPercentDigitado
        : (resultado ? Number(resultado.entradaPercentual.toFixed(2)) : '');

    // Origem do desconto (#6174), conferida sobre o valor que o motor calculou.
    const errosDesconto = darDesconto && resultado
        ? validarOrigemDesconto({
            descontoCentavos: resultado.descontoDividaCentavos,
            origem: descontoOrigem,
            descricao: descontoDescricao,
            dividaVencidaCentavos: resultado.valorFaturasCentavos + resultado.multaTotalCentavos + resultado.moraTotalCentavos
                + resultado.outrosDebitosCentavos,
        })
        : [];
    const errosOutros = useMemo(
        () => (somarOutros ? validarOutrosDebitos(outrosDebitos) : []),
        [somarOutros, outrosDebitos],
    );
    // O boleto da entrada só vai depois da assinatura: vencimento curto demais
    // fazia o cliente receber o boleto em cima do vencimento ou já vencido.
    const erroVencEntrada = erroVencimentoEntrada(vencimentoEntrada, dataEmBrasilia());
    const mudarOutroDebito = (i: number, campo: Partial<OutroDebitoDigitado>) =>
        setOutrosDebitos(prev => prev.map((d, k) => (k === i ? { ...d, ...campo } : d)));
    const origemDescontoAplicada = darDesconto && resultado && resultado.descontoDividaCentavos > 0 ? descontoOrigem : null;

    const rotuloFatura = (id: string | number) =>
        selectedFaturasData.get(String(id))?.mes_referencia || `#${id}`;

    const toggleFatura = (f: FaturaPendente) => {
        const id = String(f.id);
        setSelectedFaturasData(prev => {
            const n = new Map(prev);
            if (n.has(id)) n.delete(id); else n.set(id, f);
            return n;
        });
        // Mexeu na mão: o que está marcado agora é decisão dele, não da tela.
        setAutoMarcadas(new Set());
    };

    const handleNext = () => {
        if (step === 1) {
            if (selecionadas.length === 0 && !selectedUc) {
                addToast('Selecione ao menos uma fatura ou uma Unidade Consumidora.', 'error');
                return;
            }
        }
        if (step === 2) {
            if (erroCalculo) { addToast(erroCalculo, 'error', 10000); return; }
            if (!resultado) { addToast('Informe os valores do acordo para continuar.', 'error'); return; }
            if (erroVencEntrada) { addToast(erroVencEntrada, 'error', 10000); return; }
            if (errosOutros.length > 0) { addToast(errosOutros[0], 'error', 10000); return; }
            if (errosDesconto.length > 0) { addToast(errosDesconto[0], 'error', 10000); return; }
            const erroAnexoB = errosTermo.find(e => e.startsWith('Anexo B') || e.includes('(saldo de energia)'));
            if (erroAnexoB) { addToast(erroAnexoB, 'error', 10000); return; }
        }
        setStep(s => s + 1);
    };

    const handleSalvar = async () => {
        if (!resultado) return;
        if (erroVencEntrada) {
            addToast(erroVencEntrada, 'error', 10000);
            return;
        }
        if (errosOutros.length > 0) {
            addToast(errosOutros[0], 'error', 10000);
            return;
        }
        if (errosDesconto.length > 0) {
            addToast(errosDesconto[0], 'error', 10000);
            return;
        }
        if (errosTermo.length > 0) {
            addToast(errosTermo[0], 'error', 10000);
            return;
        }
        setLoading(true);
        try {
            // Quem assina o termo é o devedor conferido no passo 3 — pré-preenchido
            // pelo cadastro de hoje (ver o effect de `assinante`), não pela fatura,
            // que guarda o nome do dia em que o boleto saiu e pode ser o titular da
            // conta de luz — foi o que pôs o termo no nome errado em 21/09/2026.
            const clienteNome = devedor.nome.trim()
                || assinante?.nome
                || faturaReferencia?.nome_cliente_boleto
                || faturaReferencia?.cliente_nome
                || selectedUc?.nome_cliente
                || null;

            // Resolve a UC do cadastro pelo código da fatura, quando não veio escolhida.
            // Compara pelas colunas normalizadas: um `ilike` no texto cru não acha
            // '3.071.720.012-00' buscando '307172001200' (1 em cada 4 UCs tem
            // máscara), e o acordo nascia sem vínculo com o cadastro.
            let ucId: number | null = selectedUc?.id ?? null;
            const norm = normalizeUcLocal(ucCodigoBase);
            if (!ucId && norm) {
                const { data, error: erroUc } = await supabase
                    .from('unidades_consumidoras')
                    .select('id')
                    .or(`numero_uc_norm.eq."${norm}",outro_numero_uc_norm.eq."${norm}"`)
                    .order('id')
                    .limit(1);
                if (erroUc) throw new Error(`Não foi possível localizar a UC ${ucCodigoBase}: ${erroUc.message}`);
                if (data?.[0]) ucId = data[0].id;
            }
            // Somada no boleto mensal, a parcela é achada pela UC do acordo: o vínculo
            // tem que ser o da UC que a tela conferiu.
            const parcelasNaFaturaVigor = somarNaFatura && ucDoAcordo
                ? { defasagemMeses: ucDoAcordo.defasagem }
                : null;
            if (parcelasNaFaturaVigor) ucId = ucDoAcordo!.id;

            const criado = await criarAcordo({
                faturas: selecionadas,
                unidadeConsumidoraId: ucId,
                ucCodigo: ucCodigoBase.trim(),
                clienteNome,
                tipoContrato: negociarSaldo ? 'divida_saldo' : 'divida',
                saldo: negociarSaldo && saldoKwh !== '' && saldoTarifa !== ''
                    ? { kwh: Number(saldoKwh), tarifa: Number(saldoTarifa), descontoPercent: Number(saldoDesconto) || 0 }
                    : null,
                multaPercent: cobrarEncargos ? Number(multaPercent) || 0 : 0,
                moraMensalPercent: cobrarEncargos ? Number(moraPercent) || 0 : 0,
                resultado,
                descricao,
                usuario: { id: user?.id, nome: profile?.full_name ?? user?.email ?? null },
                termoDados: dadosTermo,
                parcelasNaFaturaVigor,
            });

            criado.avisos.forEach(a => addToast(a, 'warning', 12000));

            if (enviarTermo && criado.status === 'aguardando_assinatura') {
                try {
                    const resposta = await enviarTermoAcordo(
                        criado.id,
                        {
                            devedor: { email: devedor.email, whatsapp: devedor.whatsapp },
                            coobrigado: temCoobrigado ? { email: coobrigado.email, whatsapp: coobrigado.whatsapp } : null,
                        },
                        { id: user?.id, nome: profile?.full_name ?? null },
                    );
                    addToast(
                        (temCoobrigado
                            ? 'Acordo criado e termo enviado para o titular e o responsável assinarem'
                            : 'Acordo criado e termo enviado para o cliente assinar') + descreverCanal(resposta) + '.',
                        resposta.canal_devedor === 'email' ? 'warning' : 'success', 10000,
                    );
                    // A entrada sai junto com o termo: diz na hora se o boleto chegou.
                    const entrada = mensagemEnvioEntrada(resposta.entrada);
                    if (entrada) addToast(entrada.texto, entrada.tipo, 15000);
                } catch (errTermo: any) {
                    // O acordo existe; só o termo falhou. Não desfazer nada — a
                    // tela de Parcelamentos tem o botão para reenviar.
                    addToast(
                        `Acordo criado, mas o termo NÃO foi enviado: ${errTermo.message}. Reenvie pela tela de Parcelamentos.`,
                        'warning', 15000,
                    );
                }
            } else {
                addToast('Acordo criado como proposta. Envie o termo para o cliente assinar.', 'success', 10000);
            }

            addToast(
                'As faturas antigas continuam vencidas e cobráveis até o cliente assinar e pagar a entrada. Só então viram "Parcelado".',
                'info', 14000,
            );

            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'parcelamentos', acao: 'criar_acordo_com_juros', origem: 'acao' });
            onSuccess?.();
            onClose();
        } catch (err: any) {
            console.error(err);
            addToast(`Erro ao criar o acordo: ${err.message || err}`, 'error', 12000);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Faturas da busca + as já escolhidas que saíram do resultado atual (o
    // operador troca o termo de busca e não pode perder o que marcou).
    //
    // A comparação é String() dos DOIS lados de propósito: `faturasPendentes`
    // vem do banco com `id` numérico e `selectedFaturasData` guarda o objeto
    // como veio. Comparar `String(f.id) === s.id` nunca casava — número nunca é
    // igual a string — e cada fatura marcada aparecia DUAS vezes na lista.
    const idsNaBusca = new Set(faturasPendentes.map(f => String(f.id)));
    const listaFaturas = [
        ...faturasPendentes,
        ...selecionadas.filter(s => !idsNaBusca.has(String(s.id))),
    ];

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" style={{ zIndex: 9999 }} {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] animate-fade-in" onClick={e => e.stopPropagation()}>

                {/* Cabeçalho */}
                <div className="px-6 py-4 bg-gradient-to-r from-primary/10 to-transparent border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 rounded-xl text-primary"><Banknote size={22} /></div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Novo Acordo de Parcelamento</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Com juros embutidos, entrada obrigatória e termo assinado
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Passos */}
                <div className="px-8 py-3 bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700 flex items-center gap-4">
                    <StepIndicator currentStep={step} stepNumber={1} title="Dívida" />
                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                    <StepIndicator currentStep={step} stepNumber={2} title="Condições" />
                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                    <StepIndicator currentStep={step} stepNumber={3} title="Termo e conferência" />
                </div>

                <div className="flex-1 overflow-y-auto p-6">

                    {/* ============================================ PASSO 1 */}
                    {step === 1 ? (
                        <div className="space-y-5 animate-fade-in">
                            <div>
                                <label className={ROTULO}><Search size={12} className="text-primary" /> Cliente ou Unidade Consumidora</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Nome do cliente, número da UC ou CPF/CNPJ (mínimo 3 caracteres)"
                                        value={faturaSearchTerm}
                                        onChange={(e) => setFaturaSearchTerm(e.target.value)}
                                        className={`${CAMPO} pl-10`}
                                        autoFocus={!preSelectedFatura && !preSelectedUc}
                                    />
                                    <Search className="absolute left-3.5 top-3 text-gray-400" size={16} />
                                </div>
                            </div>

                            {loadingFaturas || lendoAtalho ? (
                                <div className="p-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                                    <Loader2 className="animate-spin text-primary" size={16} />
                                    {lendoAtalho ? 'Carregando as faturas escolhidas…' : 'Buscando faturas…'}
                                </div>
                            ) : null}

                            {/* O atalho trouxe fatura que a busca nunca deixaria entrar:
                                a tela diz qual e por quê, em vez de sumir com ela calada. */}
                            {atalhoFora.length > 0 ? (
                                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-2">
                                    <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                    <div className="text-xs text-amber-800 dark:text-amber-300 space-y-1">
                                        <p className="font-bold">
                                            {atalhoFora.length === 1 ? '1 fatura ficou fora do acordo' : `${atalhoFora.length} faturas ficaram fora do acordo`}
                                        </p>
                                        <ul className="list-disc pl-4 space-y-0.5">
                                            {atalhoFora.map(f => <li key={f.id}><span>{f.mes} — {f.motivo}</span></li>)}
                                        </ul>
                                    </div>
                                </div>
                            ) : null}

                            {listaFaturas.length > 0 ? (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900/60 text-[11px] font-bold uppercase tracking-wide text-gray-400 flex items-center justify-between gap-3">
                                        <span>Faturas em aberto e vencidas</span>
                                        <span className="flex items-center gap-2 normal-case tracking-normal">
                                            <span className="font-bold text-gray-500 dark:text-gray-300">
                                                {selecionadas.length} selecionada(s) · {formatarBRL(totalSelecionadoCentavos)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => { setSelectedFaturasData(new Map()); setAutoMarcadas(new Set()); }}
                                                className="px-2 py-0.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[11px] font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                            >
                                                Limpar
                                            </button>
                                        </span>
                                    </div>
                                    <div className="max-h-72 overflow-y-auto">
                                        {agruparPorPagador(listaFaturas).map(grupo => (
                                            <GrupoPagador
                                                key={String(grupo[0].id)}
                                                grupo={grupo}
                                                marcadas={selectedFaturasData}
                                                onToggle={toggleFatura}
                                                onMarcarGrupo={marcarGrupo}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {pagadoresNaLista > 1 ? (
                                <div className="p-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-xl flex items-start gap-2">
                                    <Info size={15} className="text-gray-400 shrink-0 mt-0.5" />
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                        {pagadorMarcado ? (
                                            <>
                                                A busca trouxe {pagadoresNaLista} pagadores. Veio marcada só a dívida de{' '}
                                                <strong>{pagadorMarcado}</strong>, que é quem bate com o que você digitou — um
                                                acordo é de quem paga, e de um só.
                                            </>
                                        ) : (
                                            <>
                                                A busca trouxe {pagadoresNaLista} pagadores e nenhum bate exatamente com o que você
                                                digitou, por isso nada foi marcado sozinho. Use <strong>Marcar todas</strong> no pagador certo.
                                            </>
                                        )}
                                    </p>
                                </div>
                            ) : null}

                            {/* Aviso, não trava: o acordo é de quem paga e pode juntar
                                conta, empresa e UC diferentes. O texto diz em qual
                                conta a cobrança nova vai sair. */}
                            {avisosGateway.map((a) => (
                                <div key={a.campo} className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-2">
                                    <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-800 dark:text-amber-300">{a.mensagem}</p>
                                </div>
                            ))}

                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl flex items-start gap-2">
                                <Info size={15} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                                <p className="text-xs text-blue-800 dark:text-blue-300">
                                    Toda a dívida de quem paga já vem marcada — de todas as UCs que ele paga, mesmo as que estão
                                    no nome de outro titular —, e você pode desmarcar
                                    o que não entra no acordo. Estas faturas <strong>não</strong> serão alteradas agora:
                                    continuam vencidas e com boleto válido até o cliente assinar o termo <strong>e</strong> pagar
                                    a entrada.
                                </p>
                            </div>
                        </div>
                    ) : null}

                    {/* ============================================ PASSO 2 */}
                    {step === 2 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
                            <div className="space-y-4">
                                <div>
                                    <label className={ROTULO}><FileText size={12} className="text-primary" /> Descrição do acordo</label>
                                    <textarea value={descricao} onChange={e => setDescricaoDigitada(e.target.value)} rows={2} className={`${CAMPO} resize-none`} />
                                </div>

                                {/* encargos de mora */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={cobrarEncargos} onChange={e => setCobrarEncargos(e.target.checked)} className="accent-primary w-4 h-4 mt-0.5 shrink-0" />
                                        <span className="text-xs">
                                            <strong className="block text-gray-800 dark:text-gray-200">Cobrar multa e juros de mora do atraso</strong>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Desligado por padrão. Só incide sobre faturas realmente vencidas.
                                            </span>
                                        </span>
                                    </label>
                                    {cobrarEncargos ? (
                                        <div className="grid grid-cols-2 gap-3 mt-3">
                                            <div>
                                                <label className={ROTULO}>Multa (%)</label>
                                                <input type="number" step="0.1" value={multaPercent} onChange={e => setMultaPercent(Number(e.target.value))} className={CAMPO} />
                                            </div>
                                            <div>
                                                <label className={ROTULO}>Mora (% a.m.)</label>
                                                <input type="number" step="0.1" value={moraPercent} onChange={e => setMoraPercent(Number(e.target.value))} className={CAMPO} />
                                            </div>
                                        </div>
                                    ) : null}
                                </div>

                                {/* saldo de energia — contrato tipo B */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={negociarSaldo} onChange={e => setNegociarSaldo(e.target.checked)} className="accent-primary w-4 h-4 mt-0.5 shrink-0" />
                                        <span className="text-xs">
                                            <strong className="text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                                <Zap size={12} className="text-secondary" /> Negociar saldo de energia
                                            </strong>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                {saldoDisponivel !== null
                                                    ? <>Saldo Vigor disponível nesta UC: <strong>{saldoDisponivel.toLocaleString('pt-BR')} kWh</strong></>
                                                    : 'Sem saldo lançado nas faturas desta UC.'}
                                            </span>
                                        </span>
                                    </label>
                                    {negociarSaldo ? (
                                        <div className="grid grid-cols-3 gap-2 mt-3">
                                            <div>
                                                <label className={ROTULO}>kWh</label>
                                                <input type="number" step="0.01" value={saldoKwh} onChange={e => setSaldoKwh(e.target.value === '' ? '' : Number(e.target.value))} className={CAMPO} placeholder="0" />
                                            </div>
                                            <div>
                                                <label className={ROTULO}>Tarifa R$/kWh</label>
                                                <input type="number" step="0.000001" value={saldoTarifa} onChange={e => setSaldoTarifa(e.target.value === '' ? '' : Number(e.target.value))} className={CAMPO} placeholder="0,00" />
                                            </div>
                                            <div>
                                                <label className={ROTULO}>Desconto %</label>
                                                <input type="number" step="0.1" value={saldoDesconto} onChange={e => setSaldoDesconto(e.target.value === '' ? '' : Number(e.target.value))} className={CAMPO} />
                                            </div>
                                        </div>
                                    ) : null}
                                    {negociarSaldo ? (
                                        <CamposAnexoB
                                            anexoB={anexoB}
                                            setAnexoB={setAnexoB}
                                            faltando={errosTermo.filter(e => e.startsWith('Anexo B'))}
                                        />
                                    ) : null}
                                </div>

                                {/* outros débitos — fora do sistema, somam na dívida e saem no termo */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={somarOutros} onChange={e => setSomarOutros(e.target.checked)} className="accent-primary w-4 h-4 mt-0.5 shrink-0" />
                                        <span className="text-xs">
                                            <strong className="text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                                <Plus size={12} className="text-primary" /> Somar outros débitos
                                            </strong>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Valores que não estão no sistema, como multa de distrato ou taxa. Entram na dívida e saem no termo, um por linha.
                                            </span>
                                        </span>
                                    </label>
                                    {somarOutros ? (
                                        <div className="mt-3 space-y-2">
                                            {outrosDebitos.map((d, i) => (
                                                <div key={i} className="grid grid-cols-[1fr_7rem_auto] gap-2 items-end">
                                                    <div>
                                                        {i === 0 ? <label htmlFor={`outro-debito-descricao-${i}`} className={ROTULO}>O que é</label> : null}
                                                        <input
                                                            id={`outro-debito-descricao-${i}`}
                                                            type="text"
                                                            maxLength={120}
                                                            value={d.descricao}
                                                            onChange={e => mudarOutroDebito(i, { descricao: e.target.value })}
                                                            placeholder="Ex.: multa do distrato"
                                                            aria-label={`O que é o débito ${i + 1}`}
                                                            className={CAMPO}
                                                        />
                                                    </div>
                                                    <div>
                                                        {i === 0 ? <label htmlFor={`outro-debito-valor-${i}`} className={ROTULO}>Valor R$</label> : null}
                                                        <input
                                                            id={`outro-debito-valor-${i}`}
                                                            type="number" step="0.01" min="0"
                                                            value={d.valor}
                                                            onChange={e => mudarOutroDebito(i, { valor: e.target.value === '' ? '' : Number(e.target.value) })}
                                                            aria-label={`Valor do débito ${i + 1}`}
                                                            className={CAMPO}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setOutrosDebitos(prev => prev.filter((_, k) => k !== i))}
                                                        disabled={outrosDebitos.length === 1}
                                                        title="Tirar este débito"
                                                        aria-label={`Tirar o débito ${i + 1}`}
                                                        className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={() => setOutrosDebitos(prev => [...prev, { descricao: '', valor: '' }])}
                                                className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1"
                                            >
                                                <Plus size={12} /> Adicionar outro débito
                                            </button>
                                            {errosOutros.length > 0 ? (
                                                <ul className="text-[11px] text-amber-700 dark:text-amber-400 space-y-0.5">
                                                    {errosOutros.map(e => <li key={e}>{e}</li>)}
                                                </ul>
                                            ) : null}
                                            {resultado && resultado.outrosDebitosCentavos > 0 ? (
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                                    <span>
                                                        Somando <strong className="text-gray-800 dark:text-gray-200">{formatarBRL(resultado.outrosDebitosCentavos)}</strong> à dívida.
                                                    </span>
                                                </p>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>

                                {/* desconto na dívida — sai do total antes da entrada e dos juros */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={darDesconto} onChange={e => setDarDesconto(e.target.checked)} className="accent-primary w-4 h-4 mt-0.5 shrink-0" />
                                        <span className="text-xs">
                                            <strong className="text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                                <BadgePercent size={12} className="text-primary" /> Dar desconto na dívida
                                            </strong>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Desconto da gestão ou valor que o cliente já pagou. Sai do total (faturas, encargos, saldo e outros débitos) antes da entrada e dos juros.
                                            </span>
                                        </span>
                                    </label>
                                    {darDesconto ? (
                                        <div className="grid grid-cols-2 gap-2 mt-3">
                                            <div>
                                                <label htmlFor="desconto-valor" className={ROTULO}>Desconto R$</label>
                                                <input
                                                    id="desconto-valor"
                                                    type="number" step="0.01" min="0" value={descontoValorExibido}
                                                    onChange={e => { setDescontoModo('valor'); setDescontoValorDigitado(e.target.value === '' ? '' : Number(e.target.value)); }}
                                                    className={CAMPO}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="desconto-percentual" className={ROTULO}>Desconto %</label>
                                                <input
                                                    id="desconto-percentual"
                                                    type="number" step="0.01" min="0" max="99.99" value={descontoPercentExibido}
                                                    onChange={e => { setDescontoModo('percentual'); setDescontoPercentDigitado(e.target.value === '' ? '' : Number(e.target.value)); }}
                                                    className={CAMPO}
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                    {/* origem do desconto (#6174) — sem padrão: quem dá o desconto escolhe */}
                                    {darDesconto ? (
                                        <div className="mt-3 space-y-2">
                                            <div>
                                                <span className={ROTULO}>De onde vem o desconto?</span>
                                                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="De onde vem o desconto">
                                                    {(Object.keys(ORIGENS_DESCONTO) as OrigemDesconto[]).map(o => (
                                                        <button
                                                            key={o}
                                                            type="button"
                                                            role="radio"
                                                            aria-checked={descontoOrigem === o}
                                                            onClick={() => setDescontoOrigem(o)}
                                                            className={`px-3 py-2 rounded-xl border text-xs font-semibold text-left transition-colors ${
                                                                descontoOrigem === o
                                                                    ? 'border-primary bg-primary/10 text-primary'
                                                                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary/50'
                                                            }`}
                                                        >
                                                            {ORIGENS_DESCONTO[o].rotulo}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {descontoOrigem ? (
                                                <div>
                                                    <label htmlFor="desconto-descricao" className={ROTULO}>{ORIGENS_DESCONTO[descontoOrigem].pergunta}</label>
                                                    <input
                                                        id="desconto-descricao"
                                                        type="text"
                                                        maxLength={300}
                                                        value={descontoDescricao}
                                                        onChange={e => setDescontoDescricao(e.target.value)}
                                                        placeholder={ORIGENS_DESCONTO[descontoOrigem].exemplo}
                                                        className={CAMPO}
                                                    />
                                                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{ORIGENS_DESCONTO[descontoOrigem].destino}</p>
                                                </div>
                                            ) : null}
                                            {errosDesconto.length > 0 ? (
                                                <ul className="text-[11px] text-amber-700 dark:text-amber-400 space-y-0.5">
                                                    {errosDesconto.map(e => <li key={e}>{e}</li>)}
                                                </ul>
                                            ) : null}
                                        </div>
                                    ) : null}
                                    {darDesconto && resultado && resultado.descontoDividaCentavos > 0 ? (
                                        <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                            <span>
                                                Dívida {formatarBRL(resultado.dividaBrutaCentavos)} − {origemDescontoAplicada ? ORIGENS_DESCONTO[origemDescontoAplicada].linha.toLowerCase() : 'desconto'} {formatarBRL(resultado.descontoDividaCentavos)}
                                                {' = '}<strong className="text-gray-800 dark:text-gray-200">{formatarBRL(resultado.dividaNegociadaCentavos)}</strong>
                                            </span>
                                        </p>
                                    ) : null}
                                </div>

                                {/* entrada */}
                                <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-3">
                                    <p className="text-[11px] font-bold uppercase tracking-wide text-primary mb-2 flex items-center gap-1.5">
                                        <ShieldCheck size={13} /> Entrada — obrigatória
                                    </p>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <label className={ROTULO}>Valor R$</label>
                                            <input
                                                type="number" step="0.01" value={entradaValorExibido}
                                                onChange={e => { setEntradaModo('valor'); setEntradaValorDigitado(e.target.value === '' ? '' : Number(e.target.value)); }}
                                                className={CAMPO}
                                            />
                                        </div>
                                        <div>
                                            <label className={ROTULO}>Percentual %</label>
                                            <input
                                                type="number" step="0.01" value={entradaPercentExibido}
                                                onChange={e => { setEntradaModo('percentual'); setEntradaPercentDigitado(e.target.value === '' ? '' : Number(e.target.value)); }}
                                                className={CAMPO}
                                            />
                                        </div>
                                        <div>
                                            <label className={ROTULO}>Vencimento</label>
                                            <input type="date" value={vencimentoEntrada} min={emDias(PRAZO_MINIMO_ENTRADA_DIAS)} onChange={e => setVencimentoEntrada(e.target.value)} className={CAMPO} />
                                        </div>
                                    </div>
                                    {erroVencEntrada ? (
                                        <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">{erroVencEntrada}</p>
                                    ) : null}
                                </div>

                                {/* financiamento */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
                                        <Percent size={12} className="text-primary" /> Financiamento do saldo
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className={ROTULO}>Juros (% a.m.)</label>
                                            <input type="number" step="0.01" value={taxaJuros} onChange={e => setTaxaJuros(e.target.value === '' ? '' : Number(e.target.value))} className={CAMPO} />
                                        </div>
                                        <div>
                                            <label className={ROTULO}><Calendar size={12} className="text-primary" /> 1º vencimento</label>
                                            <input type="date" value={primeiroVencimento} onChange={e => setPrimeiroVencimento(e.target.value)} className={CAMPO} />
                                        </div>
                                    </div>
                                    <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-900 rounded-lg">
                                        {(['por_numero', 'por_valor'] as const).map(m => (
                                            <button key={m} type="button" onClick={() => setModoPrazo(m)}
                                                className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-bold transition-all ${modoPrazo === m ? 'bg-white dark:bg-gray-700 text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                                {m === 'por_numero' ? 'Por nº de parcelas' : 'Por valor da parcela'}
                                            </button>
                                        ))}
                                    </div>
                                    {modoPrazo === 'por_numero' ? (
                                        <div>
                                            <label className={ROTULO}>Número de parcelas</label>
                                            <input type="number" value={numeroParcelas} onChange={e => setNumeroParcelas(e.target.value === '' ? '' : Number(e.target.value))} className={CAMPO} />
                                        </div>
                                    ) : (
                                        <div>
                                            <label className={ROTULO}>Valor de cada parcela (R$)</label>
                                            <input type="number" step="0.01" value={valorParcela} onChange={e => setValorParcela(e.target.value === '' ? '' : Number(e.target.value))} className={CAMPO} placeholder="O sistema calcula quantos meses" />
                                        </div>
                                    )}

                                    <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                                        <p className={ROTULO}><Banknote size={12} className="text-primary" /> Como cobrar as parcelas</p>
                                        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-900 rounded-lg">
                                            {([false, true] as const).map(somar => (
                                                <button key={String(somar)} type="button"
                                                    onClick={() => setParcelasNaFatura(somar)}
                                                    disabled={somar && !!motivoNaoSomar}
                                                    className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${(somar ? somarNaFatura : !somarNaFatura) ? 'bg-white dark:bg-gray-700 text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                                    {somar ? 'Somar no boleto da Vigor' : 'Boleto separado'}
                                                </button>
                                            ))}
                                        </div>
                                        {motivoNaoSomar ? (
                                            <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                                                Cada parcela sai com boleto próprio. Não dá para somar no boleto da Vigor: {motivoNaoSomar}
                                            </p>
                                        ) : somarNaFatura ? (
                                            <p className="mt-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                                                <span>
                                                    Cada parcela entra no boleto mensal da Vigor
                                                    {faixaFaturas ? <>, da fatura de <strong>{faixaFaturas.de}</strong> à de <strong>{faixaFaturas.ate}</strong></> : null}.
                                                    {ucDoAcordo?.diaVencimento ? ` O boleto desta UC costuma vencer no dia ${ucDoAcordo.diaVencimento}: use um 1º vencimento perto disso.` : ''}
                                                    {` Se a fatura do mês não for calculada com a parcela até ${DIAS_PARA_SAIR_SEPARADA} dias antes do vencimento, ela sai em boleto separado.`}
                                                </span>
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            {/* memória de cálculo, ao vivo */}
                            <div className="lg:border-l lg:border-gray-100 lg:dark:border-gray-700 lg:pl-6">
                                {erroCalculo ? (
                                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-xl flex items-start gap-2.5">
                                        <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-800 dark:text-amber-300 font-medium">{erroCalculo}</p>
                                    </div>
                                ) : resultado ? (
                                    <MemoriaCalculoAcordo resultado={resultado} rotuloFatura={rotuloFatura} origemDesconto={origemDescontoAplicada} detalhado={false} />
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 gap-2 py-12">
                                        <Calculator size={32} />
                                        <p className="text-xs">Preencha as condições para ver a memória de cálculo.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {/* ============================================ PASSO 3 */}
                    {step === 3 && resultado ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
                            <MemoriaCalculoAcordo resultado={resultado} rotuloFatura={rotuloFatura} origemDesconto={origemDescontoAplicada} detalhado />

                            <div className="space-y-4 lg:border-l lg:border-gray-100 lg:dark:border-gray-700 lg:pl-6">
                                {/* Quem assina, na cara do operador e editável: o termo já saiu
                                    no nome errado uma vez porque esse dado só aparecia no PDF pronto. */}
                                <div className={`rounded-xl border p-3 space-y-2 ${assinante ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-900/10' : 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20'}`}>
                                    <div>
                                        <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">Devedor — titular</p>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            {assinante
                                                ? `Veio do cadastro (${assinante.origem}). Confira e corrija o que precisar.`
                                                : 'A UC não tem assinante nem cliente cadastrado: os dados vieram da fatura e podem ser do titular da conta de luz. Confira antes de mandar assinar.'}
                                        </p>
                                    </div>
                                    <CamposParte
                                        parte={devedor}
                                        onChange={(campo, valor) => setDevedor(prev => ({ ...prev, [campo]: valor }))}
                                    />
                                    {faturaComOutroNome ? (
                                        <p className="text-[11px] text-gray-600 dark:text-gray-300 pt-1.5 border-t border-emerald-200/70 dark:border-emerald-900/50">
                                            A fatura está em nome de <strong>{nomeNaFatura}</strong> — titular da conta de luz. O termo vai no
                                            nome acima. Se o certo for o outro, corrija aqui ou em Unidades.
                                        </p>
                                    ) : null}
                                    {devedorPj ? (
                                        <div className="pt-2 border-t border-emerald-200/70 dark:border-emerald-900/50 space-y-2">
                                            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Empresa: quem assina por ela</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className={ROTULO}>Representante legal</label>
                                                    <input
                                                        type="text"
                                                        value={devedor.representante?.nome ?? ''}
                                                        onChange={e => setDevedor(prev => ({ ...prev, representante: { nome: e.target.value, cpf: prev.representante?.cpf ?? '', poderes: prev.representante?.poderes ?? 'contrato social' } }))}
                                                        className={CAMPO}
                                                    />
                                                </div>
                                                <div>
                                                    <label className={ROTULO}>CPF do representante</label>
                                                    <input
                                                        type="text"
                                                        value={formatarDocumento(devedor.representante?.cpf ?? '')}
                                                        onChange={e => setDevedor(prev => ({ ...prev, representante: { nome: prev.representante?.nome ?? '', cpf: e.target.value.replace(/\D/g, '').slice(0, 11), poderes: prev.representante?.poderes ?? 'contrato social' } }))}
                                                        className={CAMPO}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className={ROTULO}>Poderes comprovados por</label>
                                                <input
                                                    type="text"
                                                    value={devedor.representante?.poderes ?? 'contrato social'}
                                                    onChange={e => setDevedor(prev => ({ ...prev, representante: { nome: prev.representante?.nome ?? '', cpf: prev.representante?.cpf ?? '', poderes: e.target.value } }))}
                                                    className={CAMPO}
                                                />
                                            </div>
                                            <label className="flex items-start gap-2.5 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={!!devedor.coobrigacaoPf}
                                                    onChange={e => setDevedor(prev => ({ ...prev, coobrigacaoPf: e.target.checked ? { endereco: '' } : null }))}
                                                    className="accent-primary w-4 h-4 mt-0.5 shrink-0"
                                                />
                                                <span className="text-xs text-gray-600 dark:text-gray-300">
                                                    O representante também garante a dívida <strong>em nome próprio</strong> (Anexo E — coobrigado pessoa física).
                                                </span>
                                            </label>
                                            {devedor.coobrigacaoPf ? (
                                                <div>
                                                    <label className={ROTULO}>Endereço do representante</label>
                                                    <input
                                                        type="text"
                                                        value={devedor.coobrigacaoPf.endereco}
                                                        onChange={e => setDevedor(prev => ({ ...prev, coobrigacaoPf: { endereco: e.target.value } }))}
                                                        className={CAMPO}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>

                                <div className={`rounded-xl border p-3 ${temCoobrigado ? 'border-primary/40 bg-primary/5' : 'border-gray-200 dark:border-gray-700'}`}>
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={temCoobrigado} onChange={e => setTemCoobrigado(e.target.checked)} className="accent-primary w-4 h-4 mt-0.5 shrink-0" />
                                        <span className="text-xs">
                                            <strong className="text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                                <UserPlus size={12} className="text-primary" /> Outra pessoa assume a dívida
                                            </strong>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Ex.: comprou o estabelecimento. O titular continua como DEVEDOR e o responsável entra no termo
                                                como COOBRIGADO solidário (Anexo D). Os dois assinam, e o acordo só vale depois das duas assinaturas.
                                            </span>
                                        </span>
                                    </label>
                                    {temCoobrigado ? (
                                        <div className="space-y-2 mt-3">
                                            <CamposParte
                                                parte={coobrigado}
                                                onChange={(campo, valor) => setCoobrigado(prev => ({ ...prev, [campo]: valor }))}
                                                endereco={
                                                    <CamposEndereco
                                                        endereco={coobrigado.enderecoPartes}
                                                        onChange={e => setCoobrigado(prev => ({ ...prev, enderecoPartes: e, endereco: enderecoTexto(e) }))}
                                                    />
                                                }
                                            />
                                            <label className="flex items-start gap-2.5 cursor-pointer pt-1">
                                                <input type="checkbox" checked={boletoNoResponsavel} onChange={e => setBoletoNoResponsavel(e.target.checked)} className="accent-primary w-4 h-4 mt-0.5 shrink-0" />
                                                <span className="text-xs text-gray-600 dark:text-gray-300">
                                                    <strong>Boletos do acordo no nome do responsável</strong> — a entrada e todas as parcelas saem no
                                                    CPF/CNPJ e no endereço dele. Desmarque se o titular continuar pagando.
                                                </span>
                                            </label>
                                            <div>
                                                <label className={ROTULO}>Por que ele assume a dívida (vai no Anexo D)</label>
                                                <input type="text" value={coobrigado.motivo} onChange={e => setCoobrigado(prev => ({ ...prev, motivo: e.target.value }))} className={CAMPO} />
                                            </div>
                                        </div>
                                    ) : null}
                                </div>

                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input type="checkbox" checked={enviarTermo} onChange={e => setEnviarTermo(e.target.checked)} className="accent-primary w-4 h-4 mt-0.5 shrink-0" />
                                        <span className="text-xs">
                                            <strong className="text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                                <Send size={12} className="text-primary" /> Enviar o termo para assinatura agora
                                            </strong>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Gera o Termo de Negociação e Confissão de Dívida{negociarSaldo ? ' com o Anexo B do saldo de energia' : ''} e
                                                manda por e-mail para {temCoobrigado ? 'o titular, o responsável' : 'o cliente'}, o procurador e as 2 testemunhas.
                                            </span>
                                        </span>
                                    </label>
                                </div>

                                {errosTermo.length > 0 ? (
                                    <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                                        <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1">Falta para gerar o termo</p>
                                        <ul className="space-y-0.5 text-xs text-amber-800 dark:text-amber-300 list-disc pl-4">
                                            {errosTermo.map(e => <li key={e}>{e}</li>)}
                                        </ul>
                                    </div>
                                ) : null}

                                <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 p-3">
                                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">O que acontece ao confirmar</p>
                                    {/* Cada item tem EXATAMENTE dois filhos: o número e um <span> com
                                        todo o texto. Sem esse <span>, o `flex` do <li> trata cada
                                        <strong> e cada trecho solto como um item de flex separado, e a
                                        frase se espalha em colunas em vez de fluir como texto. */}
                                    <ol className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                                        <li className="flex gap-2">
                                            <span className="font-bold text-primary shrink-0">1.</span>
                                            <span>
                                                O acordo é criado como <strong>proposta</strong>
                                                {resultado.outrosDebitosCentavos > 0
                                                    ? <>, somando <strong>{formatarBRL(resultado.outrosDebitosCentavos)} de outros débitos</strong></>
                                                    : null}
                                                {resultado.descontoDividaCentavos > 0
                                                    ? <>, com <strong>{fraseDesconto(origemDescontoAplicada, formatarBRL(resultado.descontoDividaCentavos))}</strong></>
                                                    : null}.
                                            </span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-bold text-primary shrink-0">2.</span>
                                            <span>O boleto da <strong>entrada</strong> de {formatarBRL(resultado.entradaCentavos)}, com vencimento em {formatDateBR(resultado.vencimentoEntrada)}{temCoobrigado && boletoNoResponsavel && coobrigado.nome.trim() ? <>, no nome de <strong>{coobrigado.nome.trim()}</strong> (e as parcelas também)</> : null}, é emitido junto com o termo e só vai para o cliente <strong>depois que ele assinar</strong>.</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-bold text-primary shrink-0">3.</span>
                                            <span>
                                                {enviarTermo
                                                    ? `O termo vai para assinatura ${temCoobrigado ? 'do titular, do responsável pela dívida' : 'do cliente'}, do procurador e das 2 testemunhas.`
                                                    : 'O termo NÃO é enviado agora — envie depois pela tela de Parcelamentos.'}
                                            </span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-bold text-primary shrink-0">4.</span>
                                            <span>As {selecionadas.length} fatura(s) antiga(s) <strong>continuam vencidas e cobráveis</strong>.</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-bold text-emerald-600 shrink-0">5.</span>
                                            <span>
                                                Quando {temCoobrigado ? 'os dois assinarem' : 'o cliente assinar'} <strong>e</strong> a entrada for paga, o acordo passa a valer sozinho: {somarNaFatura
                                                    ? <>as parcelas passam a entrar no <strong>boleto mensal da Vigor</strong>{faixaFaturas ? ` (faturas de ${faixaFaturas.de} a ${faixaFaturas.ate})` : ''}</>
                                                    : 'as parcelas viram cobrança'} e as faturas antigas viram "Parcelado".
                                            </span>
                                        </li>
                                    </ol>
                                </div>

                                {/* A escolha de somar é feita no passo 2; o responsável pela
                                    dívida, aqui. Se ele desfez a soma, é aqui que o analista vê. */}
                                {parcelasNaFatura && motivoNaoSomar ? (
                                    <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                                        <p className="text-xs text-amber-800 dark:text-amber-300">
                                            <span>As parcelas <strong>não</strong> vão somadas no boleto da Vigor — cada uma sai com boleto próprio. {motivoNaoSomar}</span>
                                        </p>
                                    </div>
                                ) : null}

                                {/* Com faturas de contas ou UCs diferentes, esta é a
                                    única informação que diz onde o dinheiro vai cair —
                                    e o momento de conferir é antes de confirmar. */}
                                {avisosGateway.length > 0 ? (
                                    <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 space-y-1.5">
                                        <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">Onde o acordo será cobrado</p>
                                        {avisosGateway.map(a => (
                                            <p key={a.campo} className="text-xs text-amber-800 dark:text-amber-300">{a.mensagem}</p>
                                        ))}
                                    </div>
                                ) : null}

                                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-2">
                                    <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-800 dark:text-amber-300">
                                        Confira a memória de cálculo ao lado antes de confirmar. Ela é exatamente o que
                                        vai impresso no termo que o cliente assina.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Rodapé */}
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
                    {step > 1 ? (
                        <button onClick={() => setStep(s => s - 1)} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white font-semibold rounded-xl transition-colors flex items-center gap-2">
                            <ArrowLeft size={16} /> Voltar
                        </button>
                    ) : (
                        <button onClick={onClose} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white font-semibold rounded-xl transition-colors">
                            Cancelar
                        </button>
                    )}

                    {resultado && step > 1 ? (
                        <div className="hidden md:flex items-baseline gap-2 text-xs text-gray-500">
                            <span>Total do acordo</span>
                            <strong className="text-base tabular-nums text-gray-900 dark:text-white">{formatarBRL(resultado.totalAcordoCentavos)}</strong>
                        </div>
                    ) : null}

                    {step < 3 ? (
                        <button
                            onClick={handleNext}
                            disabled={step === 1 ? (lendoAtalho || (selecionadas.length === 0 && !selectedUc)) : !resultado}
                            className="px-5 py-2.5 bg-primary text-white font-bold rounded-xl shadow-md shadow-primary/20 hover:translate-x-0.5 hover:shadow-lg active:translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-50 disabled:hover:translate-x-0"
                        >
                            Próximo <ArrowRight size={16} />
                        </button>
                    ) : (
                        <button
                            onClick={handleSalvar}
                            disabled={loading || !resultado}
                            className="px-6 py-2.5 bg-primary text-white font-bold rounded-xl shadow-lg shadow-primary/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading
                                ? <><Loader2 className="animate-spin" size={18} /> Criando acordo…</>
                                : <><CheckCircle size={18} /> {enviarTermo ? 'Criar acordo e enviar termo' : 'Criar proposta sem enviar termo'}</>}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

/** Anexo B do termo. A Cláusula 4.4 diz que, sem a prova do evento que tornou
 *  o saldo inútil, ele NÃO pode entrar no valor negociado — por isso são
 *  obrigatórios aqui, e não no passo do termo. */
const CamposAnexoB = ({ anexoB, setAnexoB, faltando }: {
    anexoB: AnexoBTermo;
    setAnexoB: React.Dispatch<React.SetStateAction<AnexoBTermo>>;
    /** O que ainda falta — na tela, porque o toast de erro fica atrás do modal. */
    faltando: string[];
}) => {
    const set = (campo: keyof AnexoBTermo) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setAnexoB(prev => ({ ...prev, [campo]: e.target.value }));
    return (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Anexo B — prova do evento (obrigatório)</p>
            <div>
                <label className={ROTULO}>Mês a que o saldo pertence</label>
                <input
                    type="month"
                    value={mesSaldoParaInput(anexoB.mesSaldo)}
                    onChange={e => setAnexoB(prev => ({ ...prev, mesSaldo: mesSaldoDoInput(e.target.value) }))}
                    className={CAMPO}
                />
                <p className="text-[11px] text-gray-400 mt-1">
                    Sai no termo, na origem do saldo (Anexo B).
                    {anexoB.faturaFonte && anexoB.mesSaldo !== anexoB.faturaFonte ? (
                        <>
                            {' '}A última fatura com saldo desta UC é de {anexoB.faturaFonte}:{' '}
                            <button
                                type="button"
                                onClick={() => setAnexoB(prev => ({ ...prev, mesSaldo: prev.faturaFonte }))}
                                className="text-primary font-semibold hover:underline"
                            >
                                usar este mês
                            </button>
                        </>
                    ) : null}
                </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className={ROTULO}>Evento de quebra</label>
                    <select
                        value={anexoB.evento}
                        onChange={e => setAnexoB(prev => trocarEvento(prev, e.target.value as EventoQuebra))}
                        className={CAMPO}
                    >
                        {(Object.keys(EVENTOS_QUEBRA) as EventoQuebra[]).map(k => (
                            <option key={k} value={k}>{EVENTOS_QUEBRA[k].rotulo}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={ROTULO}>Artigos do Termo de Adesão</label>
                    <input type="text" value={anexoB.artigos} onChange={set('artigos')} className={CAMPO} placeholder="Art. 11 e Art. 14" />
                </div>
            </div>
            {anexoB.evento === 'outro' ? (
                <div>
                    <label className={ROTULO}>Qual foi o evento</label>
                    <input type="text" value={anexoB.eventoDescricao} onChange={set('eventoDescricao')} className={CAMPO} />
                </div>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className={ROTULO}>Data do evento</label>
                    <input type="date" value={anexoB.dataEvento} onChange={set('dataEvento')} className={CAMPO} />
                </div>
                <div>
                    <label className={ROTULO}>Documento que comprova</label>
                    <input type="text" value={anexoB.documentoEvento} onChange={set('documentoEvento')} className={CAMPO} placeholder="Ex.: notificação de cancelamento" />
                </div>
            </div>
            <div>
                <label className={ROTULO}>Por que o saldo não pode ser usado nem estornado</label>
                <textarea value={anexoB.razao} onChange={set('razao')} rows={2} className={`${CAMPO} resize-none`} />
            </div>
            <p className="text-[11px] text-gray-400">
                Fatura-fonte: <strong>{anexoB.faturaFonte || 'sem fatura com saldo'}</strong> · {anexoB.termoAdesao}
            </p>
            {faltando.length > 0 ? (
                <ul className="text-[11px] text-amber-700 dark:text-amber-400 list-disc pl-4 space-y-0.5">
                    {faltando.map(e => <li key={e}>{e.replace(/^Anexo B: /, '')}</li>)}
                </ul>
            ) : null}
        </div>
    );
};

/** Nome, documento, endereço e contatos de uma parte do termo. `endereco`
 *  substitui o campo de texto livre (o responsável precisa do endereço em
 *  campos para o banco registrar boleto no nome dele). */
const CamposParte = ({ parte, onChange, rotuloDocumento = 'CPF/CNPJ', endereco }: {
    parte: { nome: string; documento: string; endereco: string; email: string; whatsapp: string };
    onChange: (campo: 'nome' | 'documento' | 'endereco' | 'email' | 'whatsapp', valor: string) => void;
    rotuloDocumento?: string;
    endereco?: React.ReactNode;
}) => (
    <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
            <div>
                <label className={ROTULO}>Nome / razão social</label>
                <input type="text" value={parte.nome} onChange={e => onChange('nome', e.target.value)} className={CAMPO} />
            </div>
            <div>
                <label className={ROTULO}>{rotuloDocumento}</label>
                <input type="text" value={formatarDocumento(parte.documento)} onChange={e => onChange('documento', e.target.value.replace(/\D/g, '').slice(0, 14))} className={CAMPO} />
            </div>
        </div>
        {endereco ?? (
            <div>
                <label className={ROTULO}>Endereço</label>
                <input type="text" value={parte.endereco} onChange={e => onChange('endereco', e.target.value)} className={CAMPO} />
            </div>
        )}
        <div className="grid grid-cols-2 gap-2">
            <div>
                <label className={ROTULO}>E-mail (assina por aqui)</label>
                <input type="email" value={parte.email} onChange={e => onChange('email', e.target.value)} className={CAMPO} placeholder="email@cliente.com" />
            </div>
            <div>
                <label className={ROTULO}>WhatsApp</label>
                <input type="text" value={parte.whatsapp} onChange={e => onChange('whatsapp', e.target.value)} className={CAMPO} placeholder="(64) 99999-9999" />
            </div>
        </div>
    </div>
);

/** Endereço em campos, com o CEP preenchendo rua, bairro, cidade e UF (BrasilAPI,
 *  a mesma do cadastro de UC). Falhar a busca não trava nada: o analista digita. */
const CamposEndereco = ({ endereco, onChange }: {
    endereco: EnderecoPagador;
    onChange: (e: EnderecoPagador) => void;
}) => {
    const [buscando, setBuscando] = useState(false);
    const set = (campo: keyof EnderecoPagador) => (e: React.ChangeEvent<HTMLInputElement>) =>
        onChange({ ...endereco, [campo]: campo === 'uf' ? e.target.value.toUpperCase().slice(0, 2) : e.target.value });
    const buscarCep = async (valor: string) => {
        const cep = valor.replace(/\D/g, '');
        if (cep.length !== 8) return;
        setBuscando(true);
        try {
            const r = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
            if (!r.ok) return;
            const d = await r.json();
            onChange({
                ...endereco,
                cep,
                logradouro: endereco.logradouro || d.street || '',
                bairro: endereco.bairro || d.neighborhood || '',
                cidade: d.city || endereco.cidade,
                uf: d.state || endereco.uf,
            });
        } catch (err) {
            console.error('[parcelamento] busca de CEP', err);
        } finally {
            setBuscando(false);
        }
    };
    return (
        <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
                <div>
                    <label className={ROTULO}>CEP {buscando ? <Loader2 size={10} className="animate-spin" /> : null}</label>
                    <input type="text" value={endereco.cep} onChange={set('cep')} onBlur={e => buscarCep(e.target.value)} className={CAMPO} placeholder="75650-000" />
                </div>
                <div className="col-span-2">
                    <label className={ROTULO}>Rua</label>
                    <input type="text" value={endereco.logradouro} onChange={set('logradouro')} className={CAMPO} />
                </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
                <div>
                    <label className={ROTULO}>Número</label>
                    <input type="text" value={endereco.numero} onChange={set('numero')} className={CAMPO} />
                </div>
                <div>
                    <label className={ROTULO}>Bairro</label>
                    <input type="text" value={endereco.bairro} onChange={set('bairro')} className={CAMPO} />
                </div>
                <div>
                    <label className={ROTULO}>Cidade</label>
                    <input type="text" value={endereco.cidade} onChange={set('cidade')} className={CAMPO} />
                </div>
                <div>
                    <label className={ROTULO}>UF</label>
                    <input type="text" value={endereco.uf} onChange={set('uf')} className={CAMPO} placeholder="GO" />
                </div>
            </div>
        </div>
    );
};

/** Faturas de UM pagador, com cabeçalho (nome, documento, total) e o botão que
 *  marca o grupo inteiro. O nome fica no cabeçalho: repetido em cada linha,
 *  ele escondia que a lista misturava pessoas diferentes. */
const GrupoPagador = ({ grupo, marcadas, onToggle, onMarcarGrupo }: {
    grupo: FaturaPendente[];
    marcadas: Map<string, FaturaPendente>;
    onToggle: (f: FaturaPendente) => void;
    onMarcarGrupo: (grupo: FaturaPendente[], marcar: boolean) => void;
}) => {
    const todas = grupo.every(f => marcadas.has(String(f.id)));
    const total = grupo.reduce((s, f) => s + paraCentavos(Number(f.valor_boleto_vigor) || 0), 0);
    const doc = docPagador(grupo[0]);
    return (
        <div className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
            <div className="px-3 py-1.5 bg-gray-50/70 dark:bg-gray-900/40 flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-xs">
                    <strong className="text-gray-800 dark:text-gray-200">{nomePagador(grupo[0]) || 'SEM NOME'}</strong>
                    <span className="text-gray-400"> · {doc ? formatarDocumento(doc) : 'sem CPF/CNPJ'} · {grupo.length} fatura(s) · {formatarBRL(total)}</span>
                </span>
                <button
                    type="button"
                    onClick={() => onMarcarGrupo(grupo, !todas)}
                    className="shrink-0 px-2 py-0.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[11px] font-semibold text-primary hover:bg-primary/10 transition-colors"
                >
                    {todas ? 'Desmarcar' : 'Marcar todas'}
                </button>
            </div>
            {grupo.map(f => {
                const marcada = marcadas.has(String(f.id));
                return (
                    <label key={f.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm transition-colors ${marcada ? 'bg-primary/5' : 'hover:bg-gray-50 dark:hover:bg-gray-900/40'}`}>
                        <input type="checkbox" checked={marcada} onChange={() => onToggle(f)} className="accent-primary w-4 h-4 shrink-0" />
                        <span className="font-mono text-xs text-gray-500 w-20 shrink-0">{f.mes_referencia}</span>
                        <span className="flex-1 min-w-0 truncate text-xs text-gray-400">
                            UC {f.uc_codigo}
                            {titularDiferente(f) ? <span className="ml-2">· titular: {f.cliente_nome}</span> : null}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 font-semibold ${f.status_boleto_vigor === 'Vencido' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {f.status_boleto_vigor}
                        </span>
                        <span className="text-xs text-gray-400 shrink-0 w-20 text-right hidden sm:inline">{formatDateBR(f.vencimento_boleto)}</span>
                        <span className="font-semibold tabular-nums shrink-0 w-24 text-right text-gray-900 dark:text-white">
                            {formatarBRL(paraCentavos(Number(f.valor_boleto_vigor) || 0))}
                        </span>
                    </label>
                );
            })}
        </div>
    );
};

const StepIndicator = ({ currentStep, stepNumber, title }: { currentStep: number; stepNumber: number; title: string }) => {
    const concluido = currentStep > stepNumber;
    const ativo = currentStep === stepNumber;
    return (
        <div className="flex items-center gap-2.5 select-none">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${concluido ? 'bg-primary text-white ring-2 ring-primary/20' : ativo ? 'bg-primary/10 text-primary border border-primary font-black ring-2 ring-primary/10' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>
                {concluido ? '✓' : stepNumber}
            </div>
            <span className={`text-xs font-bold hidden sm:inline whitespace-nowrap transition-colors ${ativo ? 'text-gray-900 dark:text-white' : concluido ? 'text-primary' : 'text-gray-400'}`}>
                {title}
            </span>
        </div>
    );
};

export default CriarParcelamentoModal;
